// SelectFile.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Upload,
  FileText,
  Table,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "../../AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";

const FILE_TYPES = [
  { value: "user", label: "User Data" },
  { value: "market_intel", label: "Market Intelligence" },
  { value: "supplier_performance", label: "Supplier Performance" },
  { value: "other", label: "Other" },
];
const FILE_TYPE_LABELS = FILE_TYPES.reduce(
  (acc, t) => ({ ...acc, [t.value]: t.label }),
  {}
);

const CLIENT_NAME_STORAGE_KEY = "agentkai_client_names";

const normalizeClientId = (name = "") =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

const SelectFile = () => {
  const navigate = useNavigate();
  const { email } = useAuth();
  const { workspace, setWorkspace, setWorkspaces } = useWorkspace();
  // const email = "ashanm01@atkearney.com";
    // const apiBase = process.env.REACT_APP_API_BASE || "http://localhost:5000";
  const apiBase = "https://webappagentkai.azurewebsites.net/";

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(
    () => workspace?.clientId || null
  );
  const [selectedClientName, setSelectedClientName] = useState(
    () => workspace?.clientName || null
  );
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  // Add near other state declarations
  const [fileDescriptions, setFileDescriptions] = useState({});

  // New state for typed uploads
  const [newUploads, setNewUploads] = useState([
    { id: 1, type: "user", file: null, description: "" },
  ]);

  // Client-level quick upload state
  const [uploadTypeByClient, setUploadTypeByClient] = useState({});
  const [clientNames, setClientNames] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CLIENT_NAME_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const rememberClientName = useCallback((clientId, name) => {
    const normalizedId = (clientId || "").trim();
    const trimmedName = (name || "").trim();

    if (!normalizedId || !trimmedName) return;

    setClientNames((prev) => {
      if (prev[normalizedId] === trimmedName) return prev;
      const next = { ...prev, [normalizedId]: trimmedName };
      try {
        sessionStorage.setItem(
          CLIENT_NAME_STORAGE_KEY,
          JSON.stringify(next)
        );
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  }, []);

  const friendlyNameFor = useCallback(
    (clientId) => {
      if (!clientId) return "default";
      const stored = clientNames[clientId];
      if (stored && stored.trim()) return stored.trim();
      return clientId;
    },
    [clientNames]
  );

  const applyWorkspaceSelection = useCallback(
    (clientId, nameOverride) => {
      if (!clientId) return;
      const resolvedName = (nameOverride || friendlyNameFor(clientId)).trim();
      setSelectedClient(clientId);
      setSelectedClientName(resolvedName);
      setWorkspace({
        clientId,
        sessionId: "default",
        sessionDisplayId: "default",
        clientName: resolvedName,
      });
    },
    [friendlyNameFor, setWorkspace]
  );

  useEffect(() => {
    if (!workspace) return;
    if (workspace.clientId && workspace.clientId !== selectedClient) {
      setSelectedClient(workspace.clientId);
    }
    if (
      workspace.clientName &&
      workspace.clientName !== selectedClientName
    ) {
      setSelectedClientName(workspace.clientName);
    }
  }, [workspace, selectedClient, selectedClientName]);

  useEffect(() => {
    const simplified = clients.map((client) => ({
      id: client.clientId,
      name: friendlyNameFor(client.clientId),
    }));
    setWorkspaces(simplified);
  }, [clients, friendlyNameFor, setWorkspaces]);

  const generatedClientId = normalizeClientId(newClientName);

  // Fetch all files and tables grouped by client_id and session_id
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [filesRes, tablesRes] = await Promise.all([
        fetch(`${apiBase}/orc/user-files?email=${encodeURIComponent(email)}`),
        fetch(`${apiBase}/orc/agent-tables?email=${encodeURIComponent(email)}`),
      ]);

      const filesData = await filesRes.json();
      const tablesData = await tablesRes.json();

      if (!filesRes.ok)
        throw new Error(filesData.error || "Failed to fetch files");
      if (!tablesRes.ok)
        throw new Error(tablesData.error || "Failed to fetch tables");

      // Group by client_id and collect general files separately
      const clientsMap = {};

      const getClientEntry = (clientId) => {
        const cid = clientId || "default";
        if (!clientsMap[cid]) {
          clientsMap[cid] = {
            clientId: cid,
            generalFiles: [],
            _generalLookup: new Set(),
            files: [],
            tables: [],
          };
        }
        return clientsMap[cid];
      };

      const normalizeFile = (file) => ({
        ...file,
        displayName:
          file.leaf_name ||
          file.name ||
          file.filename ||
          file.blob_name?.split("/").pop(),
        fileType: file.file_type || "other",
      });

      const addGeneralFile = (file) => {
        const entry = getClientEntry(file?.client_id);
        const key =
          file?.blob_name ||
          file?.relative_path ||
          file?.leaf_name ||
          JSON.stringify(file);
        if (entry._generalLookup.has(key)) return;
        entry._generalLookup.add(key);
        entry.generalFiles.push(normalizeFile(file));
      };

      // Collect general files from the folders structure when available
      (filesData.folders || []).forEach((folder) => {
        const cid = folder?.client_id || "default";
        (folder?.general_files || []).forEach((group) => {
          (group?.files || []).forEach((f) => {
            addGeneralFile({ ...f, client_id: cid });
          });
        });
      });

      // Process files that belong to specific sessions (skip general-use sessions)
      (filesData.items || []).forEach((file) => {
        const isGeneralFile =
          file?.session_id?.startsWith("general_use_files") ||
          file?.prefix?.includes("General_Use_Files");

        if (isGeneralFile) {
          addGeneralFile(file);
          return;
        }

        const entry = getClientEntry(file?.client_id);
        entry.files.push(normalizeFile(file));
      });

      // Process tables
      (tablesData.items || []).forEach((table) => {
        const entry = getClientEntry(table?.client_id);
        entry.tables.push(table);
      });

      // Convert to array format and strip internal helpers
      const clientsArray = Object.values(clientsMap).map(
        ({ _generalLookup, ...client }) => client
      );

      setClients(clientsArray);
    } catch (err) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (
    event,
    clientId,
    fileType = "user",
    description = "" // Add description parameter
  ) => {
    const file = event.target.files?.[0];

    const normalizedClientId = (clientId || "default").trim();
    const normalizedSessionId = "default";

    if (!file) return;

    setUploadingFile(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", fileType);

      // Add description if provided
      if (description && description.trim()) {
        formData.append("file_description", description.trim());
      }

      const url =
        `${apiBase}/orc/user-files` +
        `?email=${encodeURIComponent(email)}` +
        `&client_id=${encodeURIComponent(normalizedClientId)}` +
        `&session_id=${encodeURIComponent(normalizedSessionId)}`;

      const res = await fetch(url, { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");

      await fetchData();

      // Clear the description after successful upload
      const key = normalizedClientId;
      setFileDescriptions((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } catch (err) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploadingFile(false);
      event.target.value = "";
    }
  };

  const handleDeleteFiles = async (clientId) => {
    // Debug logging
    console.log("Delete requested for:", { clientId });

    if (
      !window.confirm(
        `Delete all files and tables for client "${clientId || "default"}"?`
      )
    )
      return;

    try {
      const deletePayload = {
        email,
        client_id: clientId,
        session_id: "default",
        delete_all: true,
      };

      console.log("Sending delete request with payload:", deletePayload);

      const [filesResult, tablesResult] = await Promise.all([
        fetch(`${apiBase}/orc/user-files`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(deletePayload),
        }),
        fetch(`${apiBase}/orc/agent-tables`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(deletePayload),
        }),
      ]);

      const filesData = await filesResult.json();
      const tablesData = await tablesResult.json();

      console.log("Delete response:", { filesData, tablesData });

      if (!filesResult.ok) {
        throw new Error(filesData.error || "Failed to delete files");
      }
      if (!tablesResult.ok) {
        throw new Error(tablesData.error || "Failed to delete tables");
      }

      const totalDeleted = (filesData.deleted || 0) + (tablesData.deleted || 0);

      await fetchData();

      if (totalDeleted > 0) {
        console.log(
          `✓ Deleted ${filesData.deleted || 0} file(s) and ${
            tablesData.deleted || 0
          } table(s)`
        );
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete: " + err.message);
    }
  };

  const handleNext = () => {
    if (!selectedClient) {
      alert("Please select a client");
      return;
    }

    applyWorkspaceSelection(selectedClient, selectedClientName);

    // navigate("/agentKAI/orchestrator");
    navigate("/procure_assist/orchestrator");
  };

  const toggleClient = (clientId) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const createEmptyWorkspace = async (clientId) => {
    await fetch(
      `${apiBase}/orc/user-files?email=${encodeURIComponent(
        email
      )}&client_id=${encodeURIComponent(
        clientId
      )}&session_id=default`
    );
  };

  const handleCreateAndUpload = async () => {
    const clientName = newClientName.trim();
    const clientId = normalizeClientId(clientName);
    if (!clientName || !clientId) {
      alert("Please provide a valid Client Name");
      return;
    }

    setUploadingFile(true);
    setUploadError(null);

    try {
      await createEmptyWorkspace(clientId);

      for (const { file, type, description } of newUploads) {
        if (!file) continue;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("file_type", type);

        // Add description if provided
        if (description && description.trim()) {
          formData.append("file_description", description.trim());
        }

        const url =
          `${apiBase}/orc/user-files` +
          `?email=${encodeURIComponent(email)}` +
          `&client_id=${encodeURIComponent(clientId)}` +
          `&session_id=default`;

        const res = await fetch(url, { method: "POST", body: formData });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");
      }

      applyWorkspaceSelection(clientId, clientName);
      rememberClientName(clientId, clientName);
      setShowNewClientModal(false);
      setNewClientName("");
      setNewUploads([{ id: 1, type: "user", file: null, description: "" }]);

      await fetchData();
    } catch (err) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCreateEmptyWorkspace = async () => {
    const clientName = newClientName.trim();
    const clientId = normalizeClientId(clientName);

    if (!clientName || !clientId) {
      alert("Please provide a valid Client Name");
      return;
    }

    setUploadingFile(true);
    setUploadError(null);

    try {
      await createEmptyWorkspace(clientId);
      applyWorkspaceSelection(clientId, clientName);
      rememberClientName(clientId, clientName);
      setShowNewClientModal(false);
      setNewClientName("");
      setNewUploads([{ id: 1, type: "user", file: null, description: "" }]);
      await fetchData();
    } catch (err) {
      setUploadError(err.message || "Failed to create workspace");
    } finally {
      setUploadingFile(false);
    }
  };

  // Enhanced Styles with better alignment and spacing
  const styles = {
    container: {
      minHeight: "100vh",
      background:
        "radial-gradient(1200px 600px at 50% -10%, rgba(99,102,241,.10) 0%, rgba(10,10,10,0) 60%), #0a0a0a",
      color: "white",
      fontFamily: "Inter, system-ui, sans-serif",
      padding: "3rem 2rem",
    },
    header: {
      maxWidth: "1280px",
      margin: "0 auto 3rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    title: {
      fontSize: "36px",
      fontWeight: 600,
      color: "#FFFFFF",
      margin: 0,
      letterSpacing: "-0.02em",
    },
    subtitle: {
      color: "#FFFFFF99",
      marginTop: "10px",
      fontSize: "15px",
      lineHeight: "1.5",
    },
    content: {
      maxWidth: "1280px",
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "1fr 420px",
      gap: "2.5rem",
      alignItems: "start",
    },
    mainPanel: {
      background: "linear-gradient(135deg, #111213 0%, #0f0f10 100%)",
      borderRadius: "20px",
      border: "1px solid #1f1f1f",
      padding: "2rem",
      minHeight: "600px",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    },
    sidePanel: {
      background: "linear-gradient(135deg, #111213 0%, #0f0f10 100%)",
      borderRadius: "20px",
      border: "1px solid #1f1f1f",
      padding: "2rem",
      height: "fit-content",
      position: "sticky",
      top: "2rem",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    },
    clientCard: {
      background: "#141516",
      borderRadius: "16px",
      border: "1px solid #252525",
      marginBottom: "1rem",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    clientHeader: {
      padding: "1.25rem 1.5rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      transition: "background 0.2s ease",
      userSelect: "none",
    },
    fileItem: {
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
      padding: "0.625rem 0",
      fontSize: "13px",
      color: "#FFFFFFCC",
      lineHeight: "1.4",
    },
    button: {
      padding: "0.875rem 1.75rem",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.2s ease",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.625rem",
      whiteSpace: "nowrap",
    },
    primaryButton: {
      background: "linear-gradient(135deg, #BF6FE5 0%, #CA698F 100%)",
      color: "white",
      boxShadow: "0 4px 16px rgba(191, 111, 229, 0.3)",
    },
    secondaryButton: {
      background: "#1a1a1b",
      border: "1px solid #2a2a2a",
      color: "#FFFFFFDD",
    },
    iconButton: {
      width: "40px",
      height: "40px",
      borderRadius: "10px",
      border: "1px solid #2a2a2a",
      background: "#1a1a1b",
      color: "#FFFFFFCC",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      flexShrink: 0,
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.75)",
      backdropFilter: "blur(8px)",
      display: "grid",
      placeItems: "center",
      zIndex: 1000,
      padding: "2rem",
    },
    modalContent: {
      background: "linear-gradient(135deg, #111213 0%, #0f0f10 100%)",
      borderRadius: "24px",
      border: "1px solid #252525",
      padding: "2.5rem",
      width: "100%",
      maxWidth: "560px",
      boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)",
    },
    input: {
      width: "100%",
      padding: "1rem 1.25rem",
      borderRadius: "12px",
      border: "1px solid #2a2a2a",
      background: "#141516",
      color: "white",
      fontSize: "14px",
      outline: "none",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
    },
    selectionSummary: {
      background:
        "linear-gradient(135deg, rgba(230, 73, 165, 0.12) 0%, rgba(230, 73, 165, 0.06) 100%)",
      border: "1px solid rgba(230, 73, 165, 0.3)",
      borderRadius: "16px",
      padding: "1.5rem",
      marginBottom: "1.5rem",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "0.375rem",
      padding: "0.375rem 0.875rem",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    },
    emptyState: {
      textAlign: "center",
      padding: "5rem 2rem",
      color: "#FFFFFF66",
    },
    select: {
      height: "40px",
      borderRadius: "10px",
      border: "1px solid #2a2a2a",
      backgroundColor: "#141516",
      color: "white",
      padding: "0 2rem 0 0.75rem", // ⬅️ more space on the right
      fontSize: "13px",
      outline: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      appearance: "none", // ⬅️ hides default arrow
      WebkitAppearance: "none",
      MozAppearance: "none",
      backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='white' height='12' viewBox='0 0 20 20' width='12' xmlns='http://www.w3.org/2000/svg'><path d='M5 8l5 5 5-5z'/></svg>")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 0.75rem center", // ⬅️ controls arrow spacing
      backgroundSize: "12px",
    },

    typeTag: {
      marginLeft: "auto",
      fontSize: "11px",
      padding: "3px 8px",
      borderRadius: "6px",
      background: "#1a1a1b",
      border: "1px solid #2a2a2a",
      color: "#FFFFFFAA",
      whiteSpace: "nowrap",
    },
  };

  const skeletonStyles = {
    skeleton: {
      background:
        "linear-gradient(90deg, #1a1a1b 0%, #252525 50%, #1a1a1b 100%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      borderRadius: "8px",
    },
    skeletonCard: {
      background: "#141516",
      borderRadius: "16px",
      border: "1px solid #252525",
      padding: "1.25rem 1.5rem",
      marginBottom: "1rem",
    },
    skeletonLine: {
      height: "20px",
      marginBottom: "0.75rem",
      borderRadius: "6px",
    },
    skeletonCircle: {
      width: "22px",
      height: "22px",
      borderRadius: "50%",
    },
  };

  // Replace the loading return block with this:
  if (loading) {
    return (
      <div style={styles.container}>
        <style>
          {`
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>

        {/* Header Skeleton */}
        <div style={styles.header}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                ...skeletonStyles.skeleton,
                width: "300px",
                height: "36px",
                marginBottom: "10px",
              }}
            />
            <div
              style={{
                ...skeletonStyles.skeleton,
                width: "450px",
                height: "15px",
              }}
            />
          </div>
          <div
            style={{
              ...skeletonStyles.skeleton,
              width: "120px",
              height: "48px",
              borderRadius: "12px",
            }}
          />
        </div>

        {/* Main Content Skeleton */}
        <div style={styles.content}>
          {/* Left Panel */}
          <div style={styles.mainPanel}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2rem",
                paddingBottom: "1rem",
                borderBottom: "1px solid #1f1f1f",
              }}
            >
              <div
                style={{
                  ...skeletonStyles.skeleton,
                  width: "180px",
                  height: "20px",
                }}
              />
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <div
                  style={{
                    ...skeletonStyles.skeleton,
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                  }}
                />
                <div
                  style={{
                    ...skeletonStyles.skeleton,
                    width: "160px",
                    height: "40px",
                    borderRadius: "12px",
                  }}
                />
              </div>
            </div>

            {/* Skeleton Cards */}
            {[1, 2, 3].map((i) => (
              <div key={i} style={skeletonStyles.skeletonCard}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <div
                    style={{
                      ...skeletonStyles.skeleton,
                      ...skeletonStyles.skeletonCircle,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        ...skeletonStyles.skeleton,
                        width: "40%",
                        height: "16px",
                        marginBottom: "8px",
                      }}
                    />
                    <div
                      style={{
                        ...skeletonStyles.skeleton,
                        width: "60%",
                        height: "13px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      ...skeletonStyles.skeleton,
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Right Panel Skeleton */}
          <div style={styles.sidePanel}>
            <div
              style={{
                ...skeletonStyles.skeleton,
                width: "140px",
                height: "18px",
                marginBottom: "1.5rem",
              }}
            />

            <div
              style={{
                background: "#141516",
                border: "1px solid #252525",
                borderRadius: "14px",
                padding: "2rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  ...skeletonStyles.skeleton,
                  ...skeletonStyles.skeletonCircle,
                  width: "32px",
                  height: "32px",
                  margin: "0 auto 0.75rem",
                }}
              />
              <div
                style={{
                  ...skeletonStyles.skeleton,
                  width: "120px",
                  height: "14px",
                  margin: "0 auto",
                }}
              />
            </div>

            <div
              style={{
                ...skeletonStyles.skeleton,
                width: "100%",
                height: "48px",
                borderRadius: "12px",
                marginBottom: "1.5rem",
              }}
            />

            <div
              style={{
                padding: "1.25rem",
                background: "#141516",
                border: "1px solid #252525",
                borderRadius: "14px",
              }}
            >
              <div
                style={{
                  ...skeletonStyles.skeleton,
                  width: "100%",
                  height: "13px",
                  marginBottom: "0.5rem",
                }}
              />
              <div
                style={{
                  ...skeletonStyles.skeleton,
                  width: "80%",
                  height: "13px",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Select Your Workspace</h1>
          <p style={styles.subtitle}>
            Choose an existing client or create a new one to continue
          </p>
        </div>
        <button
          style={{ ...styles.button, ...styles.secondaryButton }}
          // onClick={() => navigate("/agentKAI")}
          onClick={() => navigate("/procure_assist")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#222";
            e.currentTarget.style.borderColor = "#333";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#1a1a1b";
            e.currentTarget.style.borderColor = "#2a2a2a";
          }}
        >
          <ChevronLeft size={18} />
          Home
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Left Panel - Client List */}
        <div style={styles.mainPanel}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "2rem",
              paddingBottom: "1rem",
              borderBottom: "1px solid #1f1f1f",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Your Workspaces
            </h3>
            <div
              style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}
            >
              <button
                style={styles.iconButton}
                onClick={fetchData}
                title="Refresh"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#222";
                  e.currentTarget.style.borderColor = "#333";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                  e.currentTarget.style.borderColor = "#2a2a2a";
                }}
              >
                <RefreshCw size={18} />
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowNewClientModal(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#222";
                  e.currentTarget.style.borderColor = "#E649A5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                  e.currentTarget.style.borderColor = "#2a2a2a";
                }}
              >
                <Plus size={18} />
                New Workspace
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "linear-gradient(135deg, #7f1d1d 0%, #6b1a1a 100%)",
                border: "1px solid #dc2626",
                borderRadius: "12px",
                padding: "1.25rem",
                marginBottom: "1.5rem",
                color: "#fecaca",
                fontSize: "14px",
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {clients.length === 0 ? (
            <div style={styles.emptyState}>
              <Folder
                size={56}
                style={{
                  margin: "0 auto 1.5rem",
                  opacity: 0.25,
                  color: "#E649A5",
                }}
              />
              <h4
                style={{
                  fontSize: "20px",
                  marginBottom: "0.75rem",
                  color: "#FFFFFFCC",
                }}
              >
                No workspaces found
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  marginBottom: "2rem",
                  color: "#FFFFFF66",
                }}
              >
                Create a new workspace to get started with Procurement Assistant
              </p>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => setShowNewClientModal(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 24px rgba(191, 111, 229, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 16px rgba(191, 111, 229, 0.3)";
                }}
              >
                <Plus size={18} />
                Create Workspace
              </button>
            </div>
          ) : (
            clients.map((client) => {
              const generalCount = client.generalFiles?.length || 0;
              const fileCount =
                (client.files?.length || 0) + (client.generalFiles?.length || 0);
              const tableCount = client.tables?.length || 0;
              const isExpanded = expandedClients.has(client.clientId);
              const isSelected = selectedClient === client.clientId;
              const uploadType = uploadTypeByClient[client.clientId] || "user";
              const descriptionKey = client.clientId;
              const friendlyName =
                clientNames[client.clientId] || client.clientId;
              const previewFiles = client.files?.slice(0, 3) || [];

              return (
                <div
                  key={client.clientId}
                  style={styles.clientCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#2f2f2f";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#252525";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={styles.clientHeader}
                    onClick={() => toggleClient(client.clientId)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#1a1a1b";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        flex: 1,
                      }}
                    >
                      {isExpanded ? (
                        <FolderOpen
                          size={22}
                          style={{ color: "#E649A5", flexShrink: 0 }}
                        />
                      ) : (
                        <Folder
                          size={22}
                          style={{ color: "#FFFFFF99", flexShrink: 0 }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            marginBottom: "4px",
                          }}
                        >
                          {friendlyName}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#FFFFFF55",
                            marginBottom: "2px",
                          }}
                        >
                          {client.clientId}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#FFFFFF77",
                          }}
                        >
                          {fileCount} file{fileCount === 1 ? "" : "s"} • {tableCount}
                          {" "}
                          table{tableCount === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      {isSelected ? (
                        <div
                          style={{
                            ...styles.badge,
                            background: "#E649A5",
                            color: "white",
                          }}
                        >
                          <Check size={14} />
                          Selected
                        </div>
                      ) : (
                        <button
                          style={{
                            ...styles.button,
                            ...styles.secondaryButton,
                            padding: "0.5rem 1rem",
                            fontSize: "12px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            applyWorkspaceSelection(
                              client.clientId,
                              friendlyName
                            );
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#222";
                            e.currentTarget.style.borderColor = "#E649A5";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#1a1a1b";
                            e.currentTarget.style.borderColor = "#2a2a2a";
                          }}
                        >
                          Use Workspace
                        </button>
                      )}
                      {isExpanded ? (
                        <ChevronDown
                          size={22}
                          style={{ color: "#FFFFFF77", flexShrink: 0 }}
                        />
                      ) : (
                        <ChevronRight
                          size={22}
                          style={{ color: "#FFFFFF77", flexShrink: 0 }}
                        />
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: "0 1.5rem 1.5rem" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: "0.75rem",
                          margin: "1rem 0",
                        }}
                      >
                        <div
                          style={{
                            ...styles.badge,
                            background: "#1a1a1b",
                            color: "#FFFFFFCC",
                            justifyContent: "center",
                          }}
                        >
                          <FileText size={14} />
                          {fileCount} file{fileCount === 1 ? "" : "s"}
                        </div>
                        <div
                          style={{
                            ...styles.badge,
                            background: "#1a1a1b",
                            color: "#FFFFFFCC",
                            justifyContent: "center",
                          }}
                        >
                          <Table size={14} />
                          {tableCount} table{tableCount === 1 ? "" : "s"}
                        </div>
                        {generalCount > 0 && (
                          <div
                            style={{
                              ...styles.badge,
                              background: "#1a1a1b",
                              color: "#FFFFFFCC",
                              justifyContent: "center",
                            }}
                          >
                            <Folder size={14} />
                            {generalCount} general file
                            {generalCount === 1 ? "" : "s"}
                          </div>
                        )}
                      </div>

                      {generalCount > 0 && (
                        <div
                          style={{
                            background: "#0d0d0e",
                            borderRadius: "14px",
                            border: "1px solid #222",
                            padding: "1.25rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.625rem",
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#FFFFFF",
                              marginBottom: "1rem",
                            }}
                          >
                            <FolderOpen size={16} style={{ color: "#E649A5" }} />
                            General Files
                          </div>
                          {client.generalFiles.map((file) => (
                            <div
                              key={
                                file.blob_name ||
                                file.relative_path ||
                                file.displayName
                              }
                              style={styles.fileItem}
                              title={file.displayName}
                            >
                              <FileText
                                size={16}
                                style={{ flexShrink: 0, color: "#FFFFFF99" }}
                              />
                              <span
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.displayName}
                              </span>
                              <span style={styles.typeTag}>
                                {FILE_TYPE_LABELS[file.fileType] ||
                                  file.fileType ||
                                  "Other"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {previewFiles.length > 0 && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#FFFFFF99",
                            marginBottom: "1rem",
                            paddingBottom: "1rem",
                            borderBottom: "1px solid #1f1f1f",
                          }}
                        >
                          {previewFiles.map((file, i) => (
                            <div
                              key={`${file.displayName || file.blob_name}-${i}`}
                              style={styles.fileItem}
                              title={file.displayName}
                            >
                              <FileText
                                size={16}
                                style={{ flexShrink: 0, color: "#FFFFFF99" }}
                              />
                              <span
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.displayName}
                              </span>
                              <span style={styles.typeTag}>
                                {FILE_TYPE_LABELS[file.fileType] ||
                                  file.fileType ||
                                  "Other"}
                              </span>
                            </div>
                          ))}
                          {(client.files?.length || 0) > previewFiles.length && (
                            <div
                              style={{
                                ...styles.fileItem,
                                fontStyle: "italic",
                                color: "#FFFFFF77",
                              }}
                            >
                              +{(client.files?.length || 0) - previewFiles.length} more file
                              {(client.files?.length || 0) - previewFiles.length === 1
                                ? ""
                                : "s"}
                            </div>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          background: "#0d0d0e",
                          borderRadius: "14px",
                          border: "1px solid #222",
                          padding: "1.25rem",
                          marginBottom: "1rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.625rem",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            marginBottom: "1rem",
                          }}
                        >
                          <Plus size={16} style={{ color: "#E649A5" }} />
                          Upload to this client
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "140px 1fr",
                            gap: "0.75rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <select
                            style={styles.select}
                            value={uploadType}
                            onChange={(e) =>
                              setUploadTypeByClient((prev) => ({
                                ...prev,
                                [client.clientId]: e.target.value,
                              }))
                            }
                            title="Choose file type"
                          >
                            {FILE_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.625rem",
                              padding: "0.625rem 1rem",
                              borderRadius: "10px",
                              background: "#1a1a1b",
                              border: "1px dashed #2a2a2a",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#FFFFFFCC",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#222";
                              e.currentTarget.style.borderColor = "#E649A5";
                              e.currentTarget.style.borderStyle = "solid";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#1a1a1b";
                              e.currentTarget.style.borderColor = "#2a2a2a";
                              e.currentTarget.style.borderStyle = "dashed";
                            }}
                          >
                            <Upload size={16} />
                            Upload {FILE_TYPE_LABELS[uploadType]}
                            <input
                              type="file"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                handleFileUpload(
                                  e,
                                  client.clientId,
                                  uploadType,
                                  fileDescriptions[descriptionKey] || ""
                                )
                              }
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        {uploadType === "market_intel" && (
                          <input
                            type="text"
                            style={{
                              ...styles.input,
                              padding: "0.625rem 1rem",
                              fontSize: "13px",
                            }}
                            placeholder="File description (optional)"
                            value={fileDescriptions[descriptionKey] || ""}
                            onChange={(e) =>
                              setFileDescriptions((prev) => ({
                                ...prev,
                                [descriptionKey]: e.target.value,
                              }))
                            }
                            onFocus={(e) =>
                              (e.target.style.borderColor = "#E649A5")
                            }
                            onBlur={(e) =>
                              (e.target.style.borderColor = "#2a2a2a")
                            }
                          />
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          style={styles.iconButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFiles(client.clientId);
                          }}
                          title="Delete workspace"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#dc2626";
                            e.currentTarget.style.borderColor = "#dc2626";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#1a1a1b";
                            e.currentTarget.style.borderColor = "#2a2a2a";
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
          {uploadError && (
            <div
              style={{
                background: "linear-gradient(135deg, #7f1d1d 0%, #6b1a1a 100%)",
                border: "1px solid #dc2626",
                borderRadius: "12px",
                padding: "1.25rem",
                marginTop: "1.5rem",
                color: "#fecaca",
                fontSize: "14px",
              }}
            >
              <strong>Upload failed:</strong> {uploadError}
            </div>
          )}
        </div>

        {/* Right Panel - Selection Summary */}
        <div style={styles.sidePanel}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#FFFFFF",
              marginBottom: "1.5rem",
            }}
          >
            Current Selection
          </h3>

          {selectedClient ? (
            <div style={styles.selectionSummary}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#FFFFFFAA",
                  marginBottom: "0.625rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                }}
              >
                Client Name
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "white",
                  wordBreak: "break-all",
                }}
              >
                {selectedClientName ||
                  clientNames[selectedClient] ||
                  selectedClient}
              </div>

              <div
                style={{
                  fontSize: "12px",
                  color: "#FFFFFFAA",
                  marginBottom: "0.625rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                }}
              >
                Client ID
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "white",
                  wordBreak: "break-all",
                }}
              >
                {selectedClient}
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "#141516",
                border: "1px solid #252525",
                borderRadius: "14px",
                padding: "2rem",
                marginBottom: "1.5rem",
                textAlign: "center",
              }}
            >
              <Folder
                size={32}
                style={{
                  margin: "0 auto 0.75rem",
                  opacity: 0.3,
                  color: "#FFFFFF66",
                }}
              />
              <p style={{ fontSize: "14px", color: "#FFFFFF77", margin: 0 }}>
                No workspace selected
              </p>
            </div>
          )}

          <button
            style={{
              ...styles.button,
              ...styles.primaryButton,
              width: "100%",
              opacity: selectedClient ? 1 : 0.5,
              cursor: selectedClient ? "pointer" : "not-allowed",
            }}
            onClick={handleNext}
            disabled={!selectedClient}
            onMouseEnter={(e) => {
              if (selectedClient) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(191, 111, 229, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedClient) {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 16px rgba(191, 111, 229, 0.3)";
              }
            }}
          >
            Continue to Procurement Assistant
            <ArrowRight size={18} />
          </button>

          <div
            style={{
              marginTop: "1.5rem",
              padding: "1.25rem",
              background: "#141516",
              border: "1px solid #252525",
              borderRadius: "14px",
              fontSize: "13px",
              color: "#FFFFFF88",
              lineHeight: "1.6",
            }}
          >
            <p style={{ margin: 0 }}>
              Your selected workspace will be used for all Procurement Assistant operations
              during this run.
            </p>
          </div>
        </div>
      </div>

      {/* New Workspace Modal */}
      {showNewClientModal && (
        <div style={styles.modal} onClick={() => setShowNewClientModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2rem",
              }}
            >
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  margin: 0,
                }}
              >
                Create New Workspace
              </h3>
              <button
                style={{
                  ...styles.iconButton,
                  border: "none",
                  background: "transparent",
                }}
                onClick={() => {
                  setShowNewClientModal(false);
                  setNewClientName("");
                  setNewUploads([{ id: 1, type: "user", file: null, description: "" }]);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Client Name */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#FFFFFFCC",
                  marginBottom: "0.625rem",
                }}
              >
                Client Name
              </label>
              <input
                type="text"
                style={styles.input}
                placeholder="e.g., Contoso Holdings"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                onFocus={(e) => (e.target.style.borderColor = "#E649A5")}
                onBlur={(e) => (e.target.style.borderColor = "#2a2a2a")}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#FFFFFF66",
                  marginTop: "0.5rem",
                }}
              >
                Generated client ID: {generatedClientId || "Enter a name to generate an ID"}
              </p>
            </div>

            {/* Typed uploads list */}
            <div style={{ marginBottom: "2rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#FFFFFFCC",
                  marginBottom: "0.75rem",
                }}
              >
                Upload Files (Optional)
              </label>

              {newUploads.map((row) => (
                <div key={row.id} style={{ marginBottom: "1rem" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "160px 1fr 40px",
                      gap: "0.75rem",
                      alignItems: "center",
                      marginBottom:
                        row.type === "market_intel" ? "0.5rem" : "0",
                    }}
                  >
                    {/* Type */}
                    <select
                      style={styles.select}
                      value={row.type}
                      onChange={(e) =>
                        setNewUploads((prev) =>
                          prev.map((r) =>
                            r.id === row.id ? { ...r, type: e.target.value } : r
                          )
                        )
                      }
                    >
                      {FILE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>

                    {/* File input */}
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.625rem",
                        padding: "0.625rem 1rem",
                        borderRadius: "10px",
                        background: "#1a1a1b",
                        border: "1px dashed #2a2a2a",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "#FFFFFFCC",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#222";
                        e.currentTarget.style.borderColor = "#E649A5";
                        e.currentTarget.style.borderStyle = "solid";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#1a1a1b";
                        e.currentTarget.style.borderColor = "#2a2a2a";
                        e.currentTarget.style.borderStyle = "dashed";
                      }}
                    >
                      <Upload size={16} />
                      {row.file ? row.file.name : "Choose file"}
                      <input
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          setNewUploads((prev) =>
                            prev.map((r) =>
                              r.id === row.id
                                ? { ...r, file: e.target.files?.[0] || null }
                                : r
                            )
                          )
                        }
                      />
                    </label>

                    {/* Remove button */}
                    <button
                      style={{
                        ...styles.iconButton,
                        borderColor: "#2a2a2a",
                      }}
                      onClick={() =>
                        setNewUploads((prev) =>
                          prev.length === 1
                            ? prev
                            : prev.filter((r) => r.id !== row.id)
                        )
                      }
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#dc2626";
                        e.currentTarget.style.borderColor = "#dc2626";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#1a1a1b";
                        e.currentTarget.style.borderColor = "#2a2a2a";
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Description field - only for Market Intelligence */}
                  {row.type === "market_intel" && (
                    <input
                      type="text"
                      style={{
                        ...styles.input,
                        width: "calc(100% - 160px - 0.75rem)",
                        padding: "0.625rem 1rem",
                        fontSize: "13px",
                        marginLeft: "calc(160px + 0.75rem)",
                      }}
                      placeholder="File description (optional)"
                      value={row.description || ""}
                      onChange={(e) =>
                        setNewUploads((prev) =>
                          prev.map((r) =>
                            r.id === row.id
                              ? { ...r, description: e.target.value }
                              : r
                          )
                        )
                      }
                      onFocus={(e) => (e.target.style.borderColor = "#E649A5")}
                      onBlur={(e) => (e.target.style.borderColor = "#2a2a2a")}
                    />
                  )}
                </div>
              ))}

              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  marginTop: "0.5rem",
                }}
                onClick={() =>
                  setNewUploads((prev) => [
                    ...prev,
                    {
                      id: Date.now(),
                      type: "user",
                      file: null,
                      description: "",
                    },
                  ])
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#222";
                  e.currentTarget.style.borderColor = "#E649A5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                  e.currentTarget.style.borderColor = "#2a2a2a";
                }}
              >
                <Plus size={16} />
                Add another file
              </button>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  flex: 1,
                }}
                onClick={() => {
                  setShowNewClientModal(false);
                  setNewClientName("");
                  setNewUploads([{ id: 1, type: "user", file: null, description: "" }]);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#222";
                  e.currentTarget.style.borderColor = "#333";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                  e.currentTarget.style.borderColor = "#2a2a2a";
                }}
              >
                Cancel
              </button>

              <button
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  flex: 1,
                }}
                onClick={handleCreateEmptyWorkspace}
                disabled={uploadingFile || !newClientName.trim() || !generatedClientId}
                onMouseEnter={(e) => {
                  if (!uploadingFile && newClientName.trim() && generatedClientId) {
                    e.currentTarget.style.background = "#222";
                    e.currentTarget.style.borderColor = "#E649A5";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a1a1b";
                  e.currentTarget.style.borderColor = "#2a2a2a";
                }}
              >
                Create Empty
              </button>

              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  flex: 1,
                  opacity: uploadingFile ? 0.7 : 1,
                }}
                onClick={handleCreateAndUpload}
                disabled={
                  uploadingFile ||
                  !newClientName.trim() ||
                  !generatedClientId ||
                  newUploads.every((u) => !u.file)
                }
                onMouseEnter={(e) => {
                  if (
                    !uploadingFile &&
                    newClientName.trim() &&
                    generatedClientId &&
                    !newUploads.every((u) => !u.file)
                  ) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 24px rgba(191, 111, 229, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!uploadingFile) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(191, 111, 229, 0.3)";
                  }
                }}
              >
                {uploadingFile ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Create & Upload
                  </>
                )}
              </button>
            </div>

            {uploadError && (
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #7f1d1d 0%, #6b1a1a 100%)",
                  border: "1px solid #dc2626",
                  borderRadius: "12px",
                  padding: "1.25rem",
                  marginTop: "1.5rem",
                  color: "#fecaca",
                  fontSize: "14px",
                }}
              >
                <strong>Upload failed:</strong> {uploadError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectFile;
